import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { message, Modal } from "antd";
import useOrganization from "features/company/hooks/useOrganization";
import useOptions from "features/masterData/hooks/useOptions";
import { deleteKeysByList, generateRandomString } from "utils/helper";
import * as services from "../services/workingLocationApi";
import {
  setCurrentWorkingLocation,
  setIsLoading,
  setWorkingLocation,
} from "../slices/attendanceSlice";
import { useTranslation } from "react-i18next";

const DELETE_KEYS_LIST = ["current", "pageSize", "pagination"];

function toInitialValues(initialValues = {}) {
  const {
    companyId = {},
    deviceType = {},
    departmentId = [],
    ...rest
  } = initialValues;
  const modifiedValues = {
    ...rest,
    companyId: Array.isArray(companyId)
      ? companyId.map((c) => c.companyId)
      : [companyId.companyId],
    deviceTypeId: deviceType.id,
    deviceType: deviceType.name,
    enableTimeLock: initialValues.enableTimeLock === "Y",
    automaticAddEmployee: initialValues.automaticAddEmployee === "Y",
    departmentListId: departmentId.map((d) => d.departmentId),
  };
  const deletedKeys = deleteKeysByList(modifiedValues, ["companyId"]);
  return deletedKeys;
}

function toParams(values = {}) {
  const { pagination = {}, sortBy = [] } = values;
  const modifiedParams = {
    ...values,
    enableTimeLock: values.enableTimeLock ? "Y" : "N",
    automaticAddEmployee: values.automaticAddEmployee ? "Y" : "N",
    page: pagination.page || 0,
    size: pagination.pageSize || 10,
    sortBy: sortBy
      .filter((s) => s.order !== undefined)
      .map((s) => ({
        direction: s.order === "descend" ? "desc" : "asc",
        property: s.field,
      })),
  };
  const deletedKeys = deleteKeysByList(modifiedParams, DELETE_KEYS_LIST);
  return deletedKeys;
}

function toParam_entity(values = {}) {
  // eslint-disable-next-line no-unused-vars
  const { pagination = {}, sortBy = [] } = values;
  console.log("toParam_entity", values);
  const modifiedParams = {
    ...values,
    enableTimeLock: values.enableTimeLock ? "Y" : "N",
    automaticAddEmployee: values.automaticAddEmployee ? "Y" : "N",
  };
  const deletedKeys = deleteKeysByList(modifiedParams, DELETE_KEYS_LIST);
  return deletedKeys;
}

function useWorkingLocation() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const currentCompany = useSelector((state) => state.company.currentCompany);
  const isLoading = useSelector((state) => state.attendance.isLoading);
  const workingLocationList = useSelector(
    (state) => state.attendance.workingLocationList
  );
  const currentWorkingLocation = useSelector(
    (state) => state.attendance.currentWorkingLocation
  );
  const [pagination, setPagination] = useState({ page: 1, size: 10 });
  const [sorter, setSorter] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { deviceTypeOptions = [], getDeviceTypeOptions } = useOptions();
  const {
    organizationUnderOptions = [],
    departmentOptions = [],
    getDepartment,
  } = useOrganization();

  const initialValues = useMemo(() => {
    return toInitialValues(currentWorkingLocation);
  }, [currentWorkingLocation]);


  const getWorkingLocationList = useCallback(
    async (params = {}) => {
      try {
        dispatch(setIsLoading(true));
        const response = await services.getWorkingLocationList({
          ...params,
          requestId: generateRandomString(),
          companyId: currentCompany,
        });
        dispatch(
          setWorkingLocation(response.data?.content || [])
        );
        setPagination({
          page: params.page + 1,
          size: params.size,
          range: [
            params.page * params.size + 1,
            Math.min((params.page + 1) * params.size, response.data?.totalItems),
          ],
          total: response.data?.totalItems,
        });
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, pagination, dispatch]
  );

  const handleOnEdit = useCallback(
    async (record) => {
      try {
        setIsModalOpen(true);
        dispatch(setIsLoading(true));
        const values = {
          workInLocationId: record.workInLocationId,
          requestId: generateRandomString(),
          companyId: currentCompany,
        };
        const response = await services.getWorkingLocation(values);
        dispatch(
          setCurrentWorkingLocation({
            ...response.data,
            workInLocationId: record.workInLocationId,
          })
        );
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch]
  );

  const handleOnDelete = useCallback(
    async (record) => {
      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: (i18n.language === "en"
          ? <span>Are you sure confirm to delete ?</span>
          : <span>คุณยืนยันการลบใช่หรือไม่ ?</span>
        ),
        okText: t("common.confirm"),
        cancelText: t("common.cancel"),
        onOk: async () => {
          try {
            dispatch(setIsLoading(true));
            const values = {
              workInLocationId: record.workInLocationId,
              requestId: generateRandomString(),
              companyId: currentCompany,
              statusFlag: "DELETE",
            };
            await services.deleteWorkingLocation(values);
            message.success(t("common.success"))
            const modifiedParams = toParams({
              pagination: {
                page: pagination.current - 1,
                pageSize: pagination.pageSize,
              },
              sortBy: sorter,
            });
            getWorkingLocationList(modifiedParams);
          } catch (error) {
            message.error(t("common.error"))
          } finally {
            dispatch(setIsLoading(false));
          }
        }
      })
    },
    [currentCompany, pagination, sorter, dispatch, getWorkingLocationList]
  );

  const handleOnStatus = useCallback(
    async (val, record) => {
      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: (i18n.language === "en"
          ? <span>Are you sure confirm to change status ?</span>
          : <span>คุณยืนยันการเปลี่ยนสถานะใช่หรือไม่ ?</span>
        ),
        okText: t("common.confirm"),
        cancelText: t("common.cancel"),
        onOk: async () => {
          try {
            dispatch(setIsLoading(true));
            const values = {
              workInLocationId: record.workInLocationId,
              requestId: generateRandomString(),
              companyId: currentCompany,
              statusFlag: val ? "ACTIVE" : "INACTIVE",
            };
            await services.deleteWorkingLocation(values);
            message.success(t("common.success"))
            const modifiedParams = toParams({
              pagination: {
                page: pagination.current - 1,
                pageSize: pagination.pageSize,
              },
              sortBy: sorter,
            });
            getWorkingLocationList(modifiedParams);
          } catch (error) {
            message.error(t("common.error"))
          } finally {
            dispatch(setIsLoading(false));
          }
        }
      })
    },
    [currentCompany, pagination, sorter, dispatch, getWorkingLocationList]
  );

  const handleOnCreate = useCallback(() => {
    setIsModalOpen(true);
    dispatch(setCurrentWorkingLocation({}));
  }, [setIsModalOpen, dispatch]);

  const handleCancel = useCallback(() => {
    // Modal.confirm({
    //   title: t("dialog.confirmation.header"),
    //   content: (
    //     <span>{t('are-you-sure-confirm-to-cancel')}</span>
    //   ),
    //   onOk() {
    setIsModalOpen(false);
    dispatch(setCurrentWorkingLocation({}));

    //   },
    //   okText: t("common.confirm"),
    //   cancelText: t("common.cancel"),
    // })

  }, [setIsModalOpen, dispatch]);

  const handleOnChange = useCallback(
    (tablePagination, _, tableSorter) => {
      setPagination(tablePagination);
      const modifiedSorter = Array.isArray(tableSorter)
        ? [...tableSorter]
        : [tableSorter];
      setSorter(modifiedSorter);
      const modifiedParams = toParams({
        pagination: {
          page: tablePagination.current - 1,
          pageSize: tablePagination.pageSize,
        },
        sortBy: modifiedSorter,
      });
      // TODO: If sorting or filtering has changed, Change page to 0
      getWorkingLocationList(modifiedParams);
    },
    [getWorkingLocationList]
  );

  const createWoringLocation = useCallback(
    async (values, fn = () => { }) => {
      try {
        dispatch(setIsLoading(true));
        let result = await services.createWoringLocation(
          toParam_entity(values)
        );
        // handleCancel();
        dispatch(setCurrentWorkingLocation({}));
        const modifiedParams = toParams({
          pagination: {
            page: pagination.current - 1,
            pageSize: pagination.pageSize,
          },
          sortBy: sorter,
        });

        getWorkingLocationList(modifiedParams);
        setIsModalOpen(false);
        message.success(t("common.success"))
        fn?.();


      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [pagination, sorter, dispatch, setIsModalOpen, getWorkingLocationList, setCurrentWorkingLocation]
  );

  const editWorkingLocation = useCallback(
    async (values) => {
      try {
        dispatch(setIsLoading(true));
        await services.editWorkingLocation(toParam_entity(values));
        message.success(t("common.success"))
        // handleCancel();
        dispatch(setCurrentWorkingLocation({}));
        const modifiedParams = toParams({
          pagination: {
            page: pagination.current - 1,
            pageSize: pagination.pageSize,
          },
          sortBy: sorter,
        });
        getWorkingLocationList(modifiedParams);
        setIsModalOpen(false);
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [dispatch, getWorkingLocationList, setIsModalOpen, handleCancel, pagination, sorter]
  );

  const handleOnSubmit = useCallback(
    (values) => {
      const modifiedValues = { ...values, companyId: currentCompany, departmentListId: values.departmentListId };
      // Modal.confirm({
      //   title: t("dialog.confirmation.header"),
      //   content:
      //     (i18n.language === "en" ?
      //       <p>
      //         Are you sure confirm to{" "}
      //         {values.workInLocationId ? "edit" : "create"} ?
      //       </p>
      //       :
      //       <p>
      //         คุณยืนยันการ
      //         {values.workInLocationId ? "แก้ไข" : "สร้าง"} ใช่หรือไม่ ?
      //       </p>
      //     ),
      //   onOk() {
      if (values.workInLocationId) {
        editWorkingLocation(modifiedValues);
      } else {
        createWoringLocation(modifiedValues);

      }
      //     },
      //     okText: t("common.confirm"),
      //     cancelText: t("common.cancel"),
      //   });
    },
    [currentCompany, editWorkingLocation, createWoringLocation]
  );

  const onSearch = useCallback(
    (values) => {
      values.page = 0;
      values.size = 50;
      getWorkingLocationList(values)
    },
    [currentCompany, editWorkingLocation, createWoringLocation]
  );

  useEffect(() => {
    if (typeof currentCompany !== "object") {
      const currentYear = new Date().getFullYear();
      getWorkingLocationList({
        page: 0,
        size: 10,
        year: currentYear + "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany]);

  useEffect(() => {
    getDeviceTypeOptions();
  }, [getDeviceTypeOptions]);

  useEffect(() => {
    getDepartment();
  }, [getDepartment]);

  return {
    initialValues,
    isLoading,
    pagination,
    editWorkingLocation,
    createWoringLocation,
    workingLocationList,
    currentCompany,
    currentWorkingLocation,
    organizationUnderOptions,
    isModalOpen,
    deviceTypeOptions,
    departmentOptions,
    onEdit: handleOnEdit,
    onDelete: handleOnDelete,
    onStatus: handleOnStatus,
    onCreate: handleOnCreate,
    onCancel: handleCancel,
    onChange: handleOnChange,
    onSubmit: handleOnSubmit,
    onSearch: onSearch,
  };
}

export default useWorkingLocation;
